/**
 * SSO Callback Page Component
 *
 * This component handles the callback from identity providers after SSO authentication.
 * It processes the authentication response and redirects the user appropriately.
 *
 * Routes:
 * - /auth/sso/callback - Generic callback (handles both OIDC and SAML)
 * - /auth/sso/oidc/callback - OIDC-specific callback
 * - /auth/sso/saml/callback - SAML-specific callback
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ssoService, SSOTokenResponse } from './SSOService';
import { useTenant } from '../../../shared/contexts/TenantContext';
import { useAuth } from '../../../context/AuthContext';
import { AUTH_CONFIG } from '../../../config';
import { auditorApi } from '../../../shared/services/auditorApi';

interface SSOCallbackProps {
  /** Protocol for this callback ('oidc' or 'saml') */
  protocol?: 'oidc' | 'saml';
}

/**
 * SSO Callback component that processes authentication responses
 */
export const SSOCallback: React.FC<SSOCallbackProps> = ({ protocol }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant_id: contextTenantId, setTenantId } = useTenant();
  const { login } = useAuth();

  useEffect(() => {
    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processCallback = async () => {
    try {
      // Check for error in URL params
      const errorParam = searchParams.get('error');
      if (errorParam) {
        const errorDescription = searchParams.get('error_description') || errorParam;
        throw new Error(errorDescription);
      }

      // Sprint 4: Check for auditor invitation intent
      const invitationToken = searchParams.get('invitation_token');
      const samlSessionId = searchParams.get('saml_session_id');
      
      if (invitationToken && samlSessionId) {
        // Auditor invitation acceptance flow
        console.log('🔐 Auditor invitation acceptance flow detected');
        await handleAuditorAcceptance(invitationToken, samlSessionId);
        return;
      }

      // The backend returns the token response in the page content
      // or redirects with the data. Check if we have inline data.
      const pageData = getInlineTokenData();

      if (pageData) {
        // Process token response from inline data
        handleTokenResponse(pageData);
      } else {
        // For OIDC, the callback might need to fetch tokens
        // For SAML, the response is typically in the POST body which
        // the backend handles and returns tokens

        // If no inline data, the backend should have returned JSON
        // This happens when the callback endpoint returns directly
        throw new Error('No authentication data received. Please try again.');
      }
    } catch (err) {
      console.error('SSO callback error:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setStatus('error');
    }
  };

  /**
   * Try to get token data from inline script (for SPA integration)
   */
  const getInlineTokenData = (): SSOTokenResponse | null => {
    // Check if the backend embedded token data in the page
    // This is typically done via a script tag or data attribute
    const dataElement = document.getElementById('sso-token-data');
    if (dataElement?.textContent) {
      try {
        return JSON.parse(dataElement.textContent);
      } catch {
        return null;
      }
    }

    // Check window variable
    if ((window as any).__SSO_TOKEN_RESPONSE__) {
      return (window as any).__SSO_TOKEN_RESPONSE__;
    }

    return null;
  };

  /**
   * Handle auditor invitation acceptance (Sprint 4)
   * 
   * Called when SAML callback includes invitation_token and saml_session_id.
   * Calls POST /api/v1/auditor/accept to validate and create auditor user.
   */
  const handleAuditorAcceptance = async (invitationToken: string, samlSessionId: string) => {
    try {
      console.log('📧 Accepting auditor invitation...');
      
      // Call backend to accept invitation
      const response = await auditorApi.acceptInvitation(invitationToken, samlSessionId);
      
      // Store tokens in localStorage
      localStorage.setItem(AUTH_CONFIG.tokenStorageKey, response.access_token);
      localStorage.setItem(AUTH_CONFIG.refreshTokenStorageKey, response.refresh_token);
      
      // Calculate token expiry
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + response.expires_in);
      localStorage.setItem(AUTH_CONFIG.tokenExpiryKey, expiryDate.toISOString());
      
      // Set authed flag
      sessionStorage.setItem('authed', 'true');
      
      // Extract tenant from dashboard URL
      // Format: https://{tenant}-brain.astrapeak.ai
      const dashboardUrl = new URL(response.dashboard_url);
      const hostname = dashboardUrl.hostname;
      const tenantSlug = hostname.split('.')[0].split('-')[0];
      
      setTenantId(tenantSlug);
      localStorage.setItem('tenant_id', tenantSlug);
      
      console.log('✅ Auditor invitation accepted successfully');
      setStatus('success');
      
      // Redirect to dashboard URL after brief delay
      setTimeout(() => {
        window.location.href = response.dashboard_url;
      }, 1000);
    } catch (err) {
      console.error('❌ Auditor acceptance failed:', err);
      throw err; // Re-throw to be caught by processCallback
    }
  };

  /**
   * Handle successful token response
   */
  const handleTokenResponse = (tokenResponse: SSOTokenResponse) => {
    // Process and store tokens
    ssoService.processTokenResponse(tokenResponse);

    // Update tenant context
    // Use existing context (hostname-derived) or stored value; never derive from email domain
    const storedTenantId = localStorage.getItem('tenant_id');
    const tenantId = contextTenantId || storedTenantId || 'default';

    setTenantId(tenantId);
    localStorage.setItem('tenant_id', tenantId);

    // Store tokens in localStorage for auth context to pick up
    // The AuthContext will detect the token and update isAuthenticated
    localStorage.setItem(AUTH_CONFIG.tokenStorageKey, tokenResponse.access_token);
    if (tokenResponse.refresh_token) {
      localStorage.setItem(AUTH_CONFIG.refreshTokenStorageKey, tokenResponse.refresh_token);
    }
    if (tokenResponse.user) {
      localStorage.setItem('user', JSON.stringify(tokenResponse.user));
    }

    setStatus('success');

    // Redirect based on user role
    const roles = tokenResponse.user.roles || [];
    setTimeout(() => {
      if (roles.includes('executive')) {
        navigate('/executive');
      } else if (roles.includes('analyst')) {
        navigate('/analyst');
      } else {
        navigate('/analyst'); // Default route
      }
    }, 1000); // Brief delay to show success message
  };

  /**
   * Handle retry
   */
  const handleRetry = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 p-8 bg-gray-800 rounded-lg shadow-lg text-center">
        {status === 'loading' && (
          <>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              Completing sign in...
            </h2>
            <p className="text-gray-400">
              Please wait while we verify your credentials.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/20 p-3">
                <svg
                  className="w-12 h-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white">
              Sign in successful!
            </h2>
            <p className="text-gray-400">
              Redirecting you to your dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="rounded-full bg-red-500/20 p-3">
                <svg
                  className="w-12 h-12 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white">
              Sign in failed
            </h2>
            <p className="text-red-400">
              {error || 'An error occurred during authentication.'}
            </p>
            <button
              onClick={handleRetry}
              className="mt-4 w-full py-2 px-4 border border-transparent rounded-md
                         shadow-sm text-sm font-medium text-white bg-purple-600
                         hover:bg-purple-700 focus:outline-none focus:ring-2
                         focus:ring-offset-2 focus:ring-purple-500"
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SSOCallback;
